// surfaces.ts — the ONE registry of navigable surfaces.
//
// Before this file the app carried three hand-typed subsets of itself: TopNav.NAV (7 pills),
// BottomTabBar.TABS (4 tabs) and CmdK.surfaces (6 rows). They disagreed, and the disagreement
// was load-bearing: /standup appeared in none of them and became a fully-built surface with no
// entrance, and Actions — the surface that carries the "needs you" badge — had no mobile home.
//
// It also holds the parent map. "Where am I" used to be `location.pathname.split('/')[1]`
// string-compared against the nav ids, so any address that was not literally a top-level id
// (a note, the briefing archive, an archived briefing, standup) lit nothing at all and the whole
// nav row went grey. `activeNavId` resolves a child route to the section it belongs to.
//
// RULE: if a surface is top-level it appears in every nav control. If it cannot, it is a child —
// give it a `parent`. Nothing gets a bespoke list of its own.

import { LIVE_STANDUP_ENABLED } from './flags';

export interface SurfaceDef {
  /** Route id = the FIRST path segment of its URL (`/conversations/:id` → `conversations`). */
  id: string;
  /** Nav label. */
  label: string;
  /** Palette-only detail line; keep it a plain description, not a restatement of the label. */
  blurb: string;
  /** Renders in the desktop pill row. */
  topLevel: boolean;
  /** Renders in the mobile bottom tab bar. Sources/Settings live in the avatar menu instead. */
  mobileTab: boolean;
  /** Reachable from the command palette. */
  inPalette: boolean;
  /** For a child route: the top-level id whose nav entry should read as active. */
  parent?: string;
  /** Build-time gate; a disabled surface is absent from every control. */
  enabled: boolean;
}

export const SURFACES: SurfaceDef[] = [
  { id: 'briefing', label: 'Briefing', blurb: 'your daily digest', topLevel: true, mobileTab: true, inPalette: true, enabled: true },
  // Actions owns explicit actions + the record; its badge counts everything needing you, which is
  // why it must exist on mobile — the badge cannot render on a surface with no tab.
  { id: 'actions', label: 'Actions', blurb: 'conflicts, tasks and the record', topLevel: true, mobileTab: true, inPalette: true, enabled: true },
  { id: 'ask', label: 'Ask', blurb: "question your team's knowledge", topLevel: true, mobileTab: true, inPalette: true, enabled: true },
  { id: 'live', label: 'Live', blurb: 'what the team is on right now', topLevel: true, mobileTab: false, inPalette: true, enabled: LIVE_STANDUP_ENABLED },
  { id: 'map', label: 'Knowledge', blurb: 'team topic graph', topLevel: true, mobileTab: true, inPalette: true, enabled: true },
  { id: 'people', label: 'People', blurb: 'your team', topLevel: true, mobileTab: true, inPalette: true, enabled: true },
  { id: 'sources', label: 'Sources', blurb: 'connections', topLevel: true, mobileTab: false, inPalette: true, enabled: true },

  // Settings is deliberately NOT a pill — it lives behind the avatar. It still needs an entry here
  // so the avatar can light up and the palette can reach it.
  { id: 'settings', label: 'Settings', blurb: 'account, privacy, team', topLevel: false, mobileTab: false, inPalette: true, enabled: true },

  // --- Children. Each lights its parent's nav entry. ---
  { id: 'standup', label: 'Full standup', blurb: 'what each teammate moved yesterday', topLevel: false, mobileTab: false, inPalette: true, parent: 'briefing', enabled: true },
  { id: 'briefing-history', label: 'Past briefings', blurb: 'every briefing you have been sent', topLevel: false, mobileTab: false, inPalette: true, parent: 'briefing', enabled: true },
  // A note belongs to Knowledge wherever it was opened from.
  { id: 'conversations', label: 'Note', blurb: '', topLevel: false, mobileTab: false, inPalette: false, parent: 'map', enabled: true },
  // The personal note route is only reachable from Settings' private-conversations list.
  { id: 'my-conversations', label: 'Private note', blurb: '', topLevel: false, mobileTab: false, inPalette: false, parent: 'settings', enabled: true },
];

const byId = new Map(SURFACES.map((s) => [s.id, s]));

const enabled = (s: SurfaceDef) => s.enabled;

/** Desktop pill row. */
export const topLevelSurfaces = (): SurfaceDef[] => SURFACES.filter((s) => enabled(s) && s.topLevel);

/** Mobile bottom tab bar. */
export const mobileTabSurfaces = (): SurfaceDef[] => SURFACES.filter((s) => enabled(s) && s.mobileTab);

/** Command palette rows — top-level AND children, so the palette is the complete map. */
export const paletteSurfaces = (): SurfaceDef[] => SURFACES.filter((s) => enabled(s) && s.inPalette);

/**
 * The nav entry that should read as active for a given route id.
 * A child resolves to its parent; an unknown route resolves to itself (matching nothing, which is
 * the correct answer for `/invite/:id` and `/privacy` — they are outside the nav model entirely).
 */
export function activeNavId(route: string): string {
  const surface = byId.get(route);
  if (!surface) return route;
  if (surface.parent) return surface.parent;
  return surface.id;
}
